import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileUp, FileText, Loader2, Sparkles, CheckCircle2, AlertCircle,
  ChevronRight, X, ArrowUpRight, ArrowDownRight, Minus, Layers
} from 'lucide-react';
import { uploadDocumentChunk, scoreDocument, applyDocumentScores, formatApiError } from '../services/api';
import { CATEGORY_LABELS } from '../components/ActionTracker';

const CHUNK_SIZE = 512 * 1024;
const ACCEPT = '.pdf,.docx,.txt,.md';

function ScoreDelta({ current, proposed }) {
  if (current == null) return <span className="text-xs text-slate-400 flex items-center gap-0.5"><Minus className="w-3 h-3" /> new</span>;
  if (proposed > current) return <span className="text-xs text-emerald-600 font-medium flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" /> from {current}</span>;
  if (proposed < current) return <span className="text-xs text-red-600 font-medium flex items-center gap-0.5"><ArrowDownRight className="w-3 h-3" /> from {current}</span>;
  return <span className="text-xs text-slate-400">unchanged</span>;
}

export default function DocumentScoringPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | uploading | scoring | review | applied
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);

  const pick = (f) => {
    if (!f) return;
    if (!ACCEPT.split(',').some((ext) => f.name.toLowerCase().endsWith(ext))) { setError('Supported formats: PDF, DOCX, TXT'); return; }
    if (f.size > 15 * 1024 * 1024) { setError('File must be under 15MB'); return; }
    setError('');
    setFile(f);
  };

  const toggleCategory = (k) => setCategories((prev) => (prev.includes(k) ? prev.filter((c) => c !== k) : [...prev, k]));

  const run = async () => {
    if (!file) return;
    setError('');
    setPhase('uploading');
    setProgress(0);
    const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', i);
        fd.append('total_chunks', total);
        fd.append('file', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), file.name);
        await uploadDocumentChunk(fd);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      setPhase('scoring');
      const res = await scoreDocument({ upload_id: uploadId, filename: file.name, categories: categories.length ? categories : null });
      setResult(res.data);
      const sel = {};
      Object.entries(res.data.scores).forEach(([cat, items]) => Object.keys(items).forEach((item) => { sel[`${cat}::${item}`] = true; }));
      setSelected(sel);
      setPhase('review');
    } catch (err) {
      setError(formatApiError(err));
      setPhase('idle');
    }
  };

  const apply = async () => {
    setApplying(true);
    setError('');
    try {
      const scores = {};
      Object.entries(result.scores).forEach(([cat, items]) => Object.entries(items).forEach(([item, s]) => {
        if (selected[`${cat}::${item}`]) { scores[cat] = scores[cat] || {}; scores[cat][item] = s; }
      }));
      const res = await applyDocumentScores({ scores, source_filename: result.filename });
      setApplied(res.data);
      setPhase('applied');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setApplying(false);
    }
  };

  const reset = () => { setFile(null); setResult(null); setSelected({}); setPhase('idle'); setApplied(null); setError(''); setCategories([]); };
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all"><ArrowLeft className="w-5 h-5 text-slate-500" /></Link>
            <div>
              <h1 className="font-outfit text-xl font-bold text-slate-900">Document Scoring</h1>
              <p className="text-sm text-slate-500">Upload a policy or plan — AI pre-fills your scorecard</p>
            </div>
          </div>
          <Link to="/scorecard" className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg font-medium flex items-center gap-1.5"><Layers className="w-4 h-4" /> Scorecard</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="doc-error"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}

        {(phase === 'idle' || phase === 'uploading' || phase === 'scoring') && (
          <>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
              onClick={() => phase === 'idle' && fileRef.current?.click()}
              className={`bg-white border-2 border-dashed rounded-2xl p-12 text-center transition-all ${file ? 'border-blue-300' : 'border-slate-300 hover:border-blue-400'} ${phase === 'idle' ? 'cursor-pointer' : ''}`}
              data-testid="drop-zone"
            >
              <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="file-input" />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium text-slate-900" data-testid="selected-filename">{file.name}</p>
                    <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB</p>
                  </div>
                  {phase === 'idle' && <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4 text-slate-400" /></button>}
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center"><FileUp className="w-8 h-8 text-blue-500" /></div>
                  <p className="font-medium text-slate-900">Drop a document here or click to browse</p>
                  <p className="text-sm text-slate-500 mt-1">Emergency operations plans, safety policies, board policies, insurance summaries, training records — PDF, DOCX, or TXT up to 15MB</p>
                </>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-slate-900 mb-1">Which categories does this document cover?</h3>
              <p className="text-sm text-slate-500 mb-4">Optional — leave empty and AI will detect them. Selecting focuses the analysis.</p>
              <div className="flex flex-wrap gap-2" data-testid="category-chips">
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => toggleCategory(k)} disabled={phase !== 'idle'}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${categories.includes(k) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`} data-testid={`cat-chip-${k}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {phase !== 'idle' ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-testid="scoring-progress">
                <div className="flex items-center gap-3 mb-3">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  <p className="font-medium text-blue-900">{phase === 'uploading' ? `Uploading securely... ${progress}%` : 'AI is reading your document and scoring controls...'}</p>
                </div>
                <div className="h-2 bg-white rounded-full overflow-hidden"><div className={`h-full bg-blue-600 transition-all ${phase === 'scoring' ? 'animate-pulse w-full' : ''}`} style={phase === 'uploading' ? { width: `${progress}%` } : undefined} /></div>
                {phase === 'scoring' && <p className="text-xs text-blue-700 mt-2">This usually takes 20–60 seconds for a long plan.</p>}
              </div>
            ) : (
              <button onClick={run} disabled={!file} className="w-full py-4 bg-gradient-to-r from-blue-600 to-violet-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-violet-700 disabled:opacity-40 flex items-center justify-center gap-2" data-testid="score-document-btn">
                <Sparkles className="w-5 h-5" /> Analyze &amp; Score Document
              </button>
            )}
            <p className="text-xs text-slate-400 text-center">Documents are analyzed in memory and discarded immediately after scoring. Nothing is stored.</p>
          </>
        )}

        {phase === 'review' && result && (
          <>
            <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="doc-result-summary">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">{result.document_type || 'Document'}</span>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${result.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : result.confidence === 'low' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{result.confidence} confidence</span>
                <span className="text-xs text-slate-500">{result.items_scored} controls scored · {(result.chars_analyzed / 1000).toFixed(0)}k characters read{result.truncated ? ' (truncated)' : ''}</span>
              </div>
              <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-2">{result.filename}</h3>
              <p className="text-slate-600 leading-relaxed">{result.summary}</p>
              {result.gaps?.length > 0 && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2">Gaps a strong document would close</p>
                  {result.gaps.map((g, i) => <p key={i} className="text-sm text-amber-900 flex items-start gap-2"><ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />{g}</p>)}
                </div>
              )}
            </div>

            {result.items_scored === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-500">AI could not find enough evidence to score any controls from this document.</div>
            ) : (
              Object.entries(result.scores).map(([cat, items]) => (
                <div key={cat} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`result-cat-${cat}`}>
                  <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                    <h4 className="font-outfit font-semibold text-slate-900">{CATEGORY_LABELS[cat] || cat}</h4>
                    {result.evidence?.[cat] && <p className="text-sm text-slate-500 mt-1">{result.evidence[cat]}</p>}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {Object.entries(items).map(([item, score]) => {
                      const key = `${cat}::${item}`;
                      const current = result.current_scores?.[cat]?.[item];
                      return (
                        <label key={item} className="px-6 py-3 flex items-center gap-4 hover:bg-slate-50 cursor-pointer" data-testid={`result-item-${cat}-${item.replace(/\W+/g, '-')}`}>
                          <input type="checkbox" checked={!!selected[key]} onChange={() => setSelected({ ...selected, [key]: !selected[key] })} className="w-4 h-4 rounded border-slate-300 text-blue-600" />
                          <span className="flex-1 text-sm text-slate-800">{item}</span>
                          <ScoreDelta current={current} proposed={score} />
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((n) => <span key={n} className={`w-6 h-6 rounded text-xs flex items-center justify-center font-semibold ${n === score ? (score >= 4 ? 'bg-emerald-500 text-white' : score >= 3 ? 'bg-blue-500 text-white' : 'bg-amber-500 text-white') : 'bg-slate-100 text-slate-400'}`}>{n}</span>)}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))
            )}

            <div className="flex flex-col sm:flex-row gap-3 sticky bottom-4">
              <button onClick={apply} disabled={applying || selectedCount === 0} className="flex-1 py-3.5 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg" data-testid="apply-scores-btn">
                {applying ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />} Apply {selectedCount} score{selectedCount === 1 ? '' : 's'} to Scorecard
              </button>
              <button onClick={reset} className="px-6 py-3.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50" data-testid="score-another-btn">Score another document</button>
            </div>
          </>
        )}

        {phase === 'applied' && applied && (
          <div className="bg-white border border-emerald-200 rounded-2xl p-12 text-center" data-testid="apply-success">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="w-8 h-8 text-emerald-500" /></div>
            <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-2">{applied.items_applied} control{applied.items_applied === 1 ? '' : 's'} updated</h2>
            <p className="text-slate-500 mb-6">
              {applied.created_new_scorecard ? 'A new Balanced Scorecard was started from this document.' : 'Your Balanced Scorecard has been updated.'} Overall score is now <b className="text-slate-900">{applied.overall_score}%</b>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => navigate('/scorecard')} className="px-6 py-3 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800" data-testid="view-scorecard-btn">View Scorecard</button>
              <button onClick={reset} className="px-6 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50">Score another document</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
